import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from '../theme';

const STORAGE_KEY = 'themeMode';
const ThemeModeContext = createContext(null);

// `preference` is what the user picked — 'light' | 'dark' | 'auto' — and is
// what Settings > Preferences > Appearance reads/writes (including to the
// backend, via updatePreferences). `mode` is the resolved light/dark value
// actually rendered, which tracks the OS when preference is 'auto'.
function getInitialPreference() {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'auto') return stored;
  return 'auto';
}

function resolveMode(preference) {
  if (preference === 'light' || preference === 'dark') return preference;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export const ThemeModeProvider = ({ children }) => {
  const [preference, setPreferenceState] = useState(getInitialPreference);
  const [mode, setModeState] = useState(() => resolveMode(preference));

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, preference);
    setModeState(resolveMode(preference));
  }, [preference]);

  // Live-follow the OS while the preference is 'auto'.
  useEffect(() => {
    if (preference !== 'auto' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setModeState(resolveMode('auto'));
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [preference]);

  // Set the preference explicitly — used by Settings' Light/Dark/Auto select.
  const setPreference = useCallback((next) => {
    if (next === 'light' || next === 'dark' || next === 'auto') setPreferenceState(next);
  }, []);

  // Quick topbar toggle — flips the *resolved* mode and pins it as an
  // explicit preference (stepping out of 'auto' if that was active).
  const toggleMode = useCallback(() => {
    setPreferenceState((prev) => (resolveMode(prev) === 'dark' ? 'light' : 'dark'));
  }, []);

  const theme = useMemo(() => createAppTheme(mode), [mode]);
  const value = useMemo(
    () => ({ mode, preference, setPreference, toggleMode }),
    [mode, preference, setPreference, toggleMode]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error('useThemeMode must be used within a ThemeModeProvider');
  return ctx;
};
